import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBack from '@mui/icons-material/esm/ArrowBack';
import CheckCircle from '@mui/icons-material/esm/CheckCircle';
import Done from '@mui/icons-material/esm/Done';
import PlaylistAdd from '@mui/icons-material/esm/PlaylistAdd';
import Send from '@mui/icons-material/esm/Send';
import StopCircle from '@mui/icons-material/esm/StopCircle';
import { DataTable, KPICard, Column } from '../../components/common';
import { PageHeader } from '../../components/layout';
import changeRequestsService, {
  ApprovalDecision,
  ChangeRequest,
  ChangeRequestApproval,
  ChangeRequestImpact,
  ImpactEntityType,
} from '../../services/api/changeRequestsService';

const labelize = (value: string) => value.replace(/_/g, ' ');

const statusColor: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  draft: 'default',
  in_review: 'warning',
  approved: 'info',
  rejected: 'error',
  enacted: 'success',
  cancelled: 'default',
};

const decisionColor: Record<ApprovalDecision, 'default' | 'success' | 'error'> = {
  pending: 'default',
  approved: 'success',
  rejected: 'error',
};

export default function ChangeRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<ChangeRequest | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [impactOpen, setImpactOpen] = useState(false);
  const [decisionOpen, setDecisionOpen] = useState<{ levelIndex: number; decision: 'approved' | 'rejected' } | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [impactForm, setImpactForm] = useState({
    entityType: 'task' as ImpactEntityType,
    entityId: '',
    impactDescription: '',
    enteredCostDelta: '',
    enteredScheduleDeltaDays: '',
  });

  const load = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      setData(await changeRequestsService.get(id));
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load change request');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const approvals = useMemo(() => [...(data?.approvals ?? [])].sort((a, b) => a.levelIndex - b.levelIndex), [data]);
  const currentLevel = approvals.find((approval) => approval.decision === 'pending' && approvals.filter((row) => row.levelIndex < approval.levelIndex).every((row) => row.decision === 'approved'));

  const addImpact = async () => {
    if (!data) return;
    try {
      await changeRequestsService.addImpact(data.id, {
        entityType: impactForm.entityType,
        entityId: impactForm.entityId,
        impactDescription: impactForm.impactDescription || null,
        enteredCostDelta: impactForm.enteredCostDelta ? Number(impactForm.enteredCostDelta) : null,
        enteredScheduleDeltaDays: impactForm.enteredScheduleDeltaDays ? Number(impactForm.enteredScheduleDeltaDays) : null,
      });
      setImpactOpen(false);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to add impact');
    }
  };

  const submit = async () => {
    if (!data) return;
    try {
      await changeRequestsService.submit(data.id);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to submit change request');
    }
  };

  const decide = async () => {
    if (!data || !decisionOpen) return;
    try {
      await changeRequestsService.decide(data.id, decisionOpen.levelIndex, { decision: decisionOpen.decision, notes: notes || null });
      setDecisionOpen(null);
      setNotes('');
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to decide approval');
    }
  };

  const enact = async () => {
    if (!data) return;
    try {
      await changeRequestsService.enact(data.id);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to enact change request');
    }
  };

  const cancel = async () => {
    if (!data) return;
    try {
      await changeRequestsService.cancel(data.id, notes);
      setCancelOpen(false);
      setNotes('');
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to cancel change request');
    }
  };

  const impactColumns: Column<ChangeRequestImpact>[] = [
    { id: 'entityType', label: 'Entity', minWidth: 110, format: (value: string) => labelize(value) },
    { id: 'entityId', label: 'Entity ID', minWidth: 160 },
    { id: 'impactDescription', label: 'Description', minWidth: 220, format: (value: string | null) => value ?? '-' },
    { id: 'enteredCostDelta', label: 'Cost Delta', minWidth: 120, align: 'right', format: (value: number | string | null) => value == null ? '-' : `$${Number(value).toLocaleString()}` },
    { id: 'enteredScheduleDeltaDays', label: 'Schedule Days', minWidth: 120, align: 'right', format: (value: number | null) => value ?? '-' },
    { id: 'applied', label: 'Applied', minWidth: 90, format: (value: boolean) => <Chip size="small" label={value ? 'yes' : 'no'} color={value ? 'success' : 'default'} /> },
  ];

  const approvalColumns: Column<ChangeRequestApproval>[] = [
    { id: 'levelIndex', label: 'Level', minWidth: 80 },
    { id: 'label', label: 'Label', minWidth: 160 },
    { id: 'approverRole', label: 'Approver', minWidth: 180, format: (_value, row) => row.approver ? `${row.approver.firstName ?? ''} ${row.approver.lastName ?? ''}`.trim() || row.approver.email : row.approverRole ?? '-' },
    { id: 'decision', label: 'Decision', minWidth: 120, format: (value: ApprovalDecision) => <Chip size="small" color={decisionColor[value]} label={labelize(value)} /> },
    { id: 'notes', label: 'Notes', minWidth: 180, format: (value: string | null) => value ?? '-' },
  ];

  if (loading && !data) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!data) return <Box><PageHeader title="Change Request" />{error && <Alert severity="error">{error}</Alert>}</Box>;

  return (
    <Box>
      <PageHeader
        title={`${data.crCode} - ${data.title}`}
        subtitle={labelize(data.changeType)}
        actions={<Button startIcon={<ArrowBack />} onClick={() => navigate('/change-requests')}>Back</Button>}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
        <Chip label={labelize(data.status)} color={statusColor[data.status]} />
        {currentLevel && <Chip label={`Current: ${currentLevel.label}`} color="warning" variant="outlined" />}
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}><KPICard title="Cost Roll-Up" value={data.computedCostImpact == null ? '-' : `$${Number(data.computedCostImpact).toLocaleString()}`} icon={<PlaylistAdd />} color="primary" /></Grid>
        <Grid item xs={12} md={4}><KPICard title="Schedule Impact" value={data.computedScheduleImpactDays == null ? '-' : `${data.computedScheduleImpactDays}d`} icon={<Send />} color="warning" /></Grid>
        <Grid item xs={12} md={4}><KPICard title="Tasks Affected" value={`${data.tasksAffectedCount ?? 0}`} icon={<CheckCircle />} color="info" /></Grid>
      </Grid>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
        {data.status === 'draft' && <Button variant="contained" startIcon={<Send />} onClick={submit}>Submit</Button>}
        {data.status === 'draft' && <Button variant="outlined" startIcon={<PlaylistAdd />} onClick={() => setImpactOpen(true)}>Add Impact</Button>}
        {data.status === 'approved' && <Button variant="contained" startIcon={<Done />} onClick={enact}>Enact</Button>}
        {['draft', 'in_review'].includes(data.status) && <Button color="error" variant="outlined" startIcon={<StopCircle />} onClick={() => setCancelOpen(true)}>Cancel</Button>}
        {currentLevel && (
          <>
            <Button color="success" variant="outlined" onClick={() => setDecisionOpen({ levelIndex: currentLevel.levelIndex, decision: 'approved' })}>Approve Current Level</Button>
            <Button color="error" variant="outlined" onClick={() => setDecisionOpen({ levelIndex: currentLevel.levelIndex, decision: 'rejected' })}>Reject Current Level</Button>
          </>
        )}
      </Stack>

      <Paper sx={{ p: 2, borderRadius: 1, mb: 2 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>Impacts</Typography>
        <DataTable columns={impactColumns} data={data.impacts ?? []} pagination={false} searchable={false} />
      </Paper>

      <Paper sx={{ p: 2, borderRadius: 1 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>Approval Chain</Typography>
        <DataTable columns={approvalColumns} data={approvals} pagination={false} searchable={false} />
      </Paper>

      <Dialog open={impactOpen} onClose={() => setImpactOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Impact</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: 2 }}>
          <TextField select label="Entity Type" value={impactForm.entityType} onChange={(event) => setImpactForm({ ...impactForm, entityType: event.target.value as ImpactEntityType })}>
            {(['task', 'budget_line', 'wbs', 'cost_code'] as ImpactEntityType[]).map((value) => <MenuItem key={value} value={value}>{labelize(value)}</MenuItem>)}
          </TextField>
          <TextField label="Entity ID" value={impactForm.entityId} onChange={(event) => setImpactForm({ ...impactForm, entityId: event.target.value })} />
          <TextField label="Cost Delta" type="number" value={impactForm.enteredCostDelta} onChange={(event) => setImpactForm({ ...impactForm, enteredCostDelta: event.target.value })} />
          <TextField label="Schedule Days" type="number" value={impactForm.enteredScheduleDeltaDays} onChange={(event) => setImpactForm({ ...impactForm, enteredScheduleDeltaDays: event.target.value })} />
          <TextField label="Description" value={impactForm.impactDescription} onChange={(event) => setImpactForm({ ...impactForm, impactDescription: event.target.value })} multiline minRows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImpactOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!impactForm.entityId} onClick={addImpact}>Add</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!decisionOpen} onClose={() => setDecisionOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{decisionOpen?.decision === 'approved' ? 'Approve' : 'Reject'} Approval Level</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField fullWidth label="Notes" value={notes} onChange={(event) => setNotes(event.target.value)} multiline minRows={3} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDecisionOpen(null)}>Cancel</Button>
          <Button variant="contained" color={decisionOpen?.decision === 'rejected' ? 'error' : 'primary'} onClick={decide}>Submit</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Change Request</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField fullWidth label="Reason" value={notes} onChange={(event) => setNotes(event.target.value)} multiline minRows={3} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Close</Button>
          <Button variant="contained" color="error" disabled={notes.trim().length < 10} onClick={cancel}>Cancel Request</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
